import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { X, Image as ImageIcon } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(items.map((i) => i.category)))],
    [items]
  );
  const filtered = active === "All" ? items : items.filter((i) => i.category === active);

  return (
    <div className="bg-ivory min-h-[70vh]">
      <div className="bg-maroon text-amber-50 py-16 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-amber-300 text-sm font-semibold tracking-widest uppercase mb-3">Moments from our journeys</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">Travel Gallery</h1>
          <p className="mt-4 text-amber-50/90 text-lg max-w-2xl">Real photographs from our fixed departures and expeditions — captured by our tour managers and our travelling families.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                  active === c ? "bg-maroon text-amber-50" : "bg-white text-slate-700 border border-amber-900/15 hover:bg-warm-surface"
                }`}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="gallery-empty">
            <ImageIcon size={48} className="mx-auto mb-4 text-amber-700/40" />
            <p className="text-lg font-semibold text-maroon">Photos coming soon</p>
            <p className="text-sm mt-1">Our gallery is being curated. Please check back shortly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {filtered.map((g) => (
              <button
                key={g.id}
                onClick={() => setLightbox(g)}
                className="group relative overflow-hidden rounded-xl bg-warm-surface aspect-square ring-1 ring-amber-900/10 hover:ring-amber-500/50 transition-all"
                data-testid={`gallery-item-${g.id}`}
              >
                <img
                  src={fileUrl(g.imageUrl)}
                  alt={g.caption || g.category}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-amber-200 text-[11px] font-bold uppercase tracking-widest">{g.category}</div>
                  {g.caption && <div className="text-white text-sm leading-snug line-clamp-2">{g.caption}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm p-4 flex items-center justify-center"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close">
            <X size={32} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={fileUrl(lightbox.imageUrl)} alt={lightbox.caption || lightbox.category} className="w-full max-h-[80vh] object-contain rounded-xl" />
            <div className="text-center mt-4">
              <div className="text-amber-300 text-xs font-bold uppercase tracking-widest">{lightbox.category}</div>
              {lightbox.caption && <div className="text-amber-50 text-lg mt-1">{lightbox.caption}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
